import * as React from 'react';

import classnames from 'classnames';

import {colors} from '../../colors/colors';
import type {Tab} from '../../control-bar/page-control-bar';
// @ts-expect-error - imported from .js/.jsx file
import {CheckmarkIcon} from '../../icon/svg/checkmark-icon';
import {Stack} from '../../layout/stack/stack';
import {StatusBadge} from '../../status-badge/status-badge';
import {Body} from '../../typography/body';

import styles from './vertical-tabs.module.css';

type Props = {
    tab: Tab;
    isActive: boolean;
    onClick: () => void;
    iconOnly?: boolean;
};

export function VerticalTab(props: Props) {
    const {tab, isActive, onClick, iconOnly} = props;

    const tabRef = React.useRef<HTMLButtonElement | null | undefined>(null);

    const styleNames = classnames(styles['vertical-tab'], {
        [styles['vertical-tab--selected']]: isActive,
        [styles['vertical-tab--icon-only']]: iconOnly,
        [styles['vertical-tab--disabled']]: tab.isDisabled,
    });

    // Removes focus when tab is selected not by keyboard. This allows the
    // selected state to look the same regardless of how it was selected.
    React.useEffect(() => {
        if (tabRef.current) {
            tabRef.current.blur();
        }
    }, [props.isActive]);

    const iconColor = React.useMemo(() => {
        if (tab.isDisabled) {
            return colors.greyLt;
        }

        return isActive ? colors.blue : colors.grey;
    }, [tab.isDisabled, isActive]);

    const tooltipText = tab.hideTooltip ? undefined : tab.label;

    return (
        <button
            onClick={onClick}
            autoFocus={false}
            // @ts-expect-error
            ref={tabRef}
            disabled={tab.isDisabled}
            className={styleNames}
        >
            <div className={styles['vertical-tab-content']}>
                {tab.icon ? (
                    <div
                        className='flex flex-shrink align-center'
                        data-tip={iconOnly ? tooltipText : undefined}
                        data-place='right'
                    >
                        <tab.icon
                            size={tab.isLegacyIcon ? 18 : 20}
                            fill={iconColor}
                            color={iconColor}
                            style={{color: iconColor}}
                            wrapWithDiv={false}
                        />
                    </div>
                ) : undefined}
                {!iconOnly && (
                    <div className='truncate' data-tip={tooltipText}>
                        <Body size='small' color={tab.isDisabled ? 'light' : 'medium'}>
                            {tab.label}
                        </Body>
                    </div>
                )}
            </div>
            <Stack isHorizontal={true} spacing={8}>
                {!iconOnly && tab.count && (
                    <StatusBadge level='info' size='mini' text={tab.count} />
                )}
                {!iconOnly && tab.showCheckmark && (
                    <CheckmarkIcon size={12} fill={colors.greyXlt} wrapWithDiv={false} />
                )}
            </Stack>
        </button>
    );
}
